import {
  Directive,
  ElementRef,
  HostListener,
  inject,
  input,
  ViewContainerRef,
  DestroyRef,
  effect
} from '@angular/core';
import { HoverTooltipComponent } from './hover-tooltip.component';
import { MediaMatcherService } from '@pc/shared';
import { Overlay, OverlayRef, OverlayPositionBuilder, PositionStrategy } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { DEFAULT_CDK_OVERLAY_CONNECTED_POSITIONS, setDynamicOffsets } from '@pc/shared';
import { fromEvent } from 'rxjs';
import { filter, take } from 'rxjs/operators';

@Directive({
  selector: '[pcHoverTooltip]',
  standalone: true
})
export class HoverTooltipDirective {
  public tooltipText = input.required<string>();
  public shouldShowTooltip = input.required<boolean>();
  public topPosition = input<number>(5);
  public leftPosition = input<number>(0);
  public viewportMargin = input<number>(10);
  public tooltipWidth = input<string>('');

  private destroyRef = inject(DestroyRef);
  private el = inject(ElementRef);
  private mediaMatcherService = inject(MediaMatcherService);
  private overlay = inject(Overlay);
  private positionBuilder = inject(OverlayPositionBuilder);
  private viewContainerRef = inject(ViewContainerRef);
  private pageDirection = document.dir as 'rtl' | 'ltr';
  private hasHover = this.mediaMatcherService.create('(hover: hover)', this.destroyRef);
  private overlayRef: OverlayRef;
  private tooltipPortal: ComponentPortal<HoverTooltipComponent>;

  constructor() {
    this.tooltipPortal = new ComponentPortal(HoverTooltipComponent, this.viewContainerRef);
    this.overlayRef = this.overlay.create({
      positionStrategy: this.buildPositionStrategy(),
      scrollStrategy: this.overlay.scrollStrategies.close(),
      direction: this.pageDirection
    });
    effect(() => {
      if (!this.shouldShowTooltip()) {
        this.hide();
      }
    });
  }

  @HostListener('click') onClick() {
    if (this.hasHover()) return;
    this.overlayRef.hasAttached() ? this.hide() : this.shouldShowTooltip() && this.show();
  }

  @HostListener('mouseenter') onMouseEnter() {
    this.hasHover() && this.shouldShowTooltip() && !this.overlayRef.hasAttached() && this.show();
  }

  @HostListener('mouseleave') onMouseLeave() {
    this.hasHover() && this.hide();
  }
  private show(): void {
    const tooltipComponentRef = this.overlayRef.attach(this.tooltipPortal);
    tooltipComponentRef.instance.tooltipText = this.tooltipText;
    tooltipComponentRef.instance.tooltipWidth = this.tooltipWidth;

    // Add global click listener to detect clicks outside
    this.addClickOutsideListener();
  }

  private addClickOutsideListener(): void {
    // Use take(1) to automatically complete after the first click
    fromEvent<MouseEvent>(document, 'pointerdown')
      .pipe(
        // Wait for the next click
        take(1),
        filter((event) => {
          // Check if the click was outside both the tooltip and the host element
          const clickTarget = event.target as HTMLElement;
          const hostEl = this.el.nativeElement;

          // Don't close if clicking on the host element itself
          if (hostEl.contains(clickTarget)) {
            return false;
          }

          // Don't close if clicking inside the tooltip
          const tooltipEl = this.overlayRef.overlayElement;
          if (tooltipEl && tooltipEl.contains(clickTarget)) {
            return false;
          }

          return true;
        })
      )
      .subscribe(() => {
        // If we get here, the click was outside both elements
        this.hide();
      });
  }

  private hide(): void {
    this.overlayRef?.detach();
  }

  private buildPositionStrategy(): PositionStrategy {
    const connectedPositions = setDynamicOffsets(
      DEFAULT_CDK_OVERLAY_CONNECTED_POSITIONS,
      this.topPosition(),
      this.leftPosition()
    );

    const positionStrategy = this.positionBuilder
      .flexibleConnectedTo(this.el)
      .withPositions(connectedPositions)
      .withPush(true)
      .withLockedPosition(false)
      .withViewportMargin(this.viewportMargin());

    return positionStrategy;
  }
}
